import { Observable, defer, of } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, finalize, map, switchMap, take, tap } from 'rxjs/operators';

import { OrganRecoveryConfirmationHttpService } from '@services/organ-recovery-confirmation-http.service';
import {
  OrganRecoveryConfirmationModalComponent,
  OrganRecoveryConfirmationModalData,
} from '@modals/organ-recovery-confirmation/component/organ-recovery-confirmation-modal.component';
import {
  ORGAN_RECOVERY_CONFIRMATION_GENERIC_ERROR,
  ORGAN_RECOVERY_CONFIRMATION_MODAL_WIDTH,
  ORGAN_RECOVERY_CONFIRMATION_REQUIRED_CODE,
  ORGAN_RECOVERY_CONFIRMATION_REQUIRED_MESSAGE,
} from '@modals/organ-recovery-confirmation/properties/organ-recovery-confirmation-modal.properties';

/**
 * Result of a guarded Job Done attempt.
 *
 * `dismissed` is NOT an error: it is the caller's signal that the user backed out (Cancel, the
 * header close icon, Escape, or a backdrop click) and that the transition must not happen, so the
 * caller restores the previous status. A failed save or a failed Job Done surfaces as an observable
 * error instead, because those need the server's message.
 *
 * `in-progress` is the suppressed-duplicate signal: a confirmation flow for this same service
 * request is already on screen, so this call opened no dialog and sent no request. It is kept
 * distinct from `dismissed` because the two demand opposite reactions - a dismissal is the end of
 * the only flow and the caller must restore its own state, whereas a suppressed duplicate must
 * leave every bit of state alone, since the live flow still owns it.
 */
export type OrganRecoveryConfirmationOutcome<T> =
  | { readonly outcome: 'dismissed' }
  | { readonly outcome: 'in-progress' }
  | { readonly outcome: 'completed'; readonly value: T };

/**
 * Orchestrates the organ recovery confirmation around a Job Done transition (TAC2-2006).
 *
 * Ordering is the whole point: the answer is saved FIRST and the Job Done call is chained off the
 * save with `switchMap`, so a failed save produces zero Job Done calls - the transition can never
 * run against an unanswered service request. A successful save followed by a failed Job Done leaves
 * the answer saved and the status untouched, which is exactly what a retry needs; the modal never
 * prefills, so a retry reopens unset (locked decision D4).
 */
@Injectable({
  providedIn: 'root',
})
export class OrganRecoveryConfirmationService {
  private readonly dialog = inject(MatDialog);
  private readonly httpService = inject(OrganRecoveryConfirmationHttpService);

  /**
   * Service requests with a confirmation flow currently on screen (TAC2-2006).
   *
   * Every entry point - the SR list's Job Done, the driver dashboard's status select, the leg
   * details panel's status select, and the Actions menu's Transport Confirmation - funnels through
   * this service, so one re-entrancy guard here covers all of them and cannot drift the way four
   * component-level booleans would. Keyed by service request rather than by leg on purpose: two
   * legs of the same service request ask the same single question, so the second must be suppressed
   * too.
   */
  private readonly serviceRequestsAwaitingConfirmation = new Set<string>();

  /**
   * Opens the modal, then - only on an explicit Yes/No - saves the answer and runs `jobDone`.
   *
   * A backdrop click is blocked (see `keepEveryDismissalExplicit`); Escape still resolves through
   * the same `undefined` path as Cancel and the close icon.
   *
   * Re-entrant calls for a service request that already has a flow open emit `in-progress` without
   * opening a second dialog or sending anything, so a double click cannot stack modals, save the
   * answer twice, or run the transition twice.
   */
  confirmThenRun<T>(
    serviceRequestId: string,
    jobDone: () => Observable<T>,
    onAnswerSaved?: (answer: boolean) => void,
  ): Observable<OrganRecoveryConfirmationOutcome<T>> {
    // `defer` so the guard is tested at subscribe time rather than at call time - the callers all
    // subscribe immediately, but that keeps the check and the `finalize` release symmetric.
    return defer(() => {
      if (this.serviceRequestsAwaitingConfirmation.has(serviceRequestId)) {
        return of<OrganRecoveryConfirmationOutcome<T>>({ outcome: 'in-progress' });
      }

      this.serviceRequestsAwaitingConfirmation.add(serviceRequestId);

      const dialogRef = this.dialog.open<OrganRecoveryConfirmationModalComponent, void, boolean | undefined>(
        OrganRecoveryConfirmationModalComponent,
        {
          width: ORGAN_RECOVERY_CONFIRMATION_MODAL_WIDTH,
          autoFocus: false,
          disableClose: true,
        },
      );

      this.keepEveryDismissalExplicit(dialogRef);

      return dialogRef.afterClosed().pipe(
        take(1),
        switchMap((answer: boolean | undefined) => {
          // Every dismissal route closes with `undefined`. Only a real boolean proceeds.
          if (answer !== true && answer !== false) {
            return of<OrganRecoveryConfirmationOutcome<T>>({ outcome: 'dismissed' });
          }

          return this.httpService.saveAnswer(serviceRequestId, answer).pipe(
            // Fires on save success, before the transition is attempted, so the caller can record the
            // stored answer even when the transition then fails - a later retry must not re-ask.
            tap(() => onAnswerSaved?.(answer)),
            switchMap(() => jobDone().pipe(map((value: T): OrganRecoveryConfirmationOutcome<T> => ({ outcome: 'completed', value })))),
          );
        }),
        // Releases on completion, on error, AND on unsubscribe (component teardown), so a failed
        // save or a destroyed page can never leave the service request permanently unguardable.
        finalize(() => this.serviceRequestsAwaitingConfirmation.delete(serviceRequestId)),
      );
    });
  }

  /**
   * Opens the modal on its own and, on an explicit Yes/No, saves the answer - no status transition.
   *
   * This is the Actions-menu ("Transport Confirmation") path: it captures or revises the answer
   * while the service request is still open, independently of completing it. `currentAnswer`
   * preselects the stored value so an existing No is visible as No rather than as unanswered.
   *
   * Emits `dismissed` without issuing any request when the user backs out, so the caller keeps the
   * value it already holds. A failed save surfaces as an observable error carrying the server's
   * message, and the caller must leave its local value untouched in that case.
   *
   * Shares the one re-entrancy guard with `confirmThenRun`, so a repeated click emits `in-progress`
   * and - just as importantly - this path cannot open a second dialog over a completion-guard flow
   * already asking the same question about the same service request.
   */
  confirmAndSave(serviceRequestId: string, currentAnswer: boolean | null): Observable<OrganRecoveryConfirmationOutcome<boolean>> {
    return defer(() => {
      if (this.serviceRequestsAwaitingConfirmation.has(serviceRequestId)) {
        return of<OrganRecoveryConfirmationOutcome<boolean>>({ outcome: 'in-progress' });
      }

      this.serviceRequestsAwaitingConfirmation.add(serviceRequestId);

      const dialogRef = this.dialog.open<OrganRecoveryConfirmationModalComponent, OrganRecoveryConfirmationModalData, boolean | undefined>(
        OrganRecoveryConfirmationModalComponent,
        {
          width: ORGAN_RECOVERY_CONFIRMATION_MODAL_WIDTH,
          autoFocus: false,
          disableClose: true,
          data: { currentAnswer },
        },
      );

      this.keepEveryDismissalExplicit(dialogRef);

      return dialogRef.afterClosed().pipe(
        take(1),
        switchMap((answer: boolean | undefined) => {
          // Every dismissal route closes with `undefined`. Only a real boolean saves.
          if (answer !== true && answer !== false) {
            return of<OrganRecoveryConfirmationOutcome<boolean>>({ outcome: 'dismissed' });
          }

          return this.httpService
            .saveAnswer(serviceRequestId, answer)
            .pipe(map((): OrganRecoveryConfirmationOutcome<boolean> => ({ outcome: 'completed', value: answer })));
        }),
        finalize(() => this.serviceRequestsAwaitingConfirmation.delete(serviceRequestId)),
      );
    });
  }

  /**
   * Makes every way out of the modal an explicit one (TAC2-2006, QA 2026-09-06).
   *
   * A stray click on the backdrop used to dismiss the question, which reads as an accidental "not
   * now" on a required field, so `disableClose` now blocks it. Material's flag also swallows
   * Escape, so Escape is wired back by hand and still closes with `undefined` - exactly what Cancel
   * and the X do - because the ticket's dev notes list it as a dismissal route. Submit is untouched:
   * it closes with the boolean answer.
   *
   * `keydownEvents()` completes when the dialog closes, so this subscription needs no teardown.
   */
  private keepEveryDismissalExplicit(dialogRef: MatDialogRef<OrganRecoveryConfirmationModalComponent, boolean | undefined>): void {
    dialogRef
      .keydownEvents()
      .pipe(filter((event: KeyboardEvent) => event.key === 'Escape'))
      .subscribe(() => dialogRef.close());
  }

  /**
   * True when a rejected transition is the organ recovery completion guard rather than any other
   * failure. Matches the V2 error code first and falls back to the shared V1 rejection text, which
   * both server routes emit from one constant.
   */
  isConfirmationRequiredError(error: unknown): boolean {
    const body = (error as { error?: unknown })?.error as { errorCode?: string; errors?: string[] } | undefined;

    if (body?.errorCode === ORGAN_RECOVERY_CONFIRMATION_REQUIRED_CODE) {
      return true;
    }

    return body?.errors?.[0] === ORGAN_RECOVERY_CONFIRMATION_REQUIRED_MESSAGE;
  }

  /**
   * Resolves the message to show for a failed save or Job Done: the V2 `errorMessage` first, then
   * the legacy `errors[0]`, then a generic fallback. Local to this feature on purpose - the global
   * error handling is left untouched.
   */
  resolveErrorMessage(error: unknown, fallback: string = ORGAN_RECOVERY_CONFIRMATION_GENERIC_ERROR): string {
    const body = (error as { error?: unknown })?.error as { errorMessage?: string; errors?: string[] } | undefined;

    if (body?.errorMessage) {
      return body.errorMessage;
    }

    if (body?.errors?.[0]) {
      return body.errors[0];
    }

    return fallback;
  }
}
